import { useState } from 'react';

import { useAPI } from 'lib/API';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { updateCreditRecord, createCreditRecord } from 'requests/credit';
import { getBusinessUnitCompanyInfo } from 'requests/businessUnitCompany';
import { emptyCreditInfo, emptyVendorCompanyInfo, VendorCompanyDetailsCredit } from 'data/VendorCompany';
import { emptyBusinessUnitCompanyDetailsCredit, BusinessUnitCompanyDetailsCredit } from 'data/BusinessUnitCompany';
import { CreditInfo, CreditTrackingMode } from 'types/credit.types';
import {
  extractCreditInfo,
  determineCreditTrackingMode
} from 'utils/credit';
import { CREDIT_SOURCES, CREDIT_MESSAGES } from 'constants/credit';

interface UseCreditProps {
  businessUnitId?: number;
  companyId: string | number;
}

const useCredit = ({ businessUnitId, companyId }: UseCreditProps) => {
  const [isConnected, setIsConnected] = useState(false);
  const [mdmId, setMdmId] = useState('');
  const [creditId, setCreditId] = useState('');
  const [creditTrackingMode, setCreditTrackingMode] = useState<CreditTrackingMode>(CreditTrackingMode.UID_REQUIRED);
  const [vendorCompanyDetails, setVendorCompanyDetails] = useState<VendorCompanyDetailsCredit>(emptyVendorCompanyInfo);
  const [businessUnitCompanyDetails, setBusinessUnitCompanyDetails] = useState<BusinessUnitCompanyDetailsCredit>(emptyBusinessUnitCompanyDetailsCredit);
  const [creditInfo, setCreditInfo] = useState<CreditInfo>(emptyCreditInfo);
  const [isInvalid, setIsInvalid] = useState(false);
  const [error, setError] = useState('');

  const businessUnitCompanyGetReq = useAPI({
    deferFn: getBusinessUnitCompanyInfo,
    onResolve: result => {
      if (result.creditId && !result.credit) return renderToast(toast.TYPE.ERROR, CREDIT_MESSAGES.ERROR_FETCHING_DETAILS);

      // Extract business unit company details
      setBusinessUnitCompanyDetails({
        creditId: result.creditId,
        businessUnitCompanyId: result.businessUnitCompanyId || '',
        id: result.id,
        address: result.company?.address || emptyBusinessUnitCompanyDetailsCredit.address
      });

      // Also populate vendorCompanyDetails for downstream components (MDMCreditCard)
      setVendorCompanyDetails({
        creditId: result.creditId,
        vendorCompanyId: result.businessUnitCompanyId || '',
        id: result.id || 0,
        address: result.company?.address || emptyVendorCompanyInfo.address
      });

      // Handle credit information using utility function
      const { creditInfo: newCreditInfo, creditId: newCreditId } = extractCreditInfo(result);
      setCreditInfo(newCreditInfo);
      setCreditId(newCreditId);

      // Set MDM ID and connection status
      setMdmId(result.credit?.source === CREDIT_SOURCES.MDM ? result.credit?.buyerExternalCode : '');
      setIsConnected(true);

      // Determine credit tracking mode based on source and status
      setCreditTrackingMode(determineCreditTrackingMode(result));
    },
    onReject: () => {
      setIsConnected(false);
    }
  });

  const reloadCompanyInfo = () => {
    businessUnitCompanyGetReq.reload();
  };

  const mdmApplyReq = useAPI({
    deferFn: createCreditRecord,
    onResolve: (result: any) => {
      if (result.credit?.buyerExternalCode) {
        error && setError('');
        setIsInvalid(false);
        renderToast(toast.TYPE.SUCCESS, CREDIT_MESSAGES.MDM_ID_ASSIGNED);
        reloadCompanyInfo();
      }
    },
    onReject: (err: any) => {
      setError(err.message);
      setIsInvalid(true);
    }
  });

  const updateCreditReq = useAPI({
    deferFn: updateCreditRecord,
    onResolve: () => {
      error && setError('');
      setIsInvalid(false);
      renderToast(toast.TYPE.SUCCESS, CREDIT_MESSAGES.CREDIT_RECORD_UPDATED);
      reloadCompanyInfo();
    },
    onReject: (err) => {
      setError(err.message);
      setIsInvalid(true);
    }
  });

  const createCreditReq = useAPI({
    deferFn: createCreditRecord,
    onResolve: () => {
      error && setError('');
      setIsInvalid(false);
      renderToast(toast.TYPE.SUCCESS, CREDIT_MESSAGES.CREDIT_RECORD_CREATED);
      reloadCompanyInfo();
    },
    onReject: (err) => {
      setError(err.message);
      setIsInvalid(true);
    }
  });

  // Function to load business unit company info if both businessUnitId and companyId are available
  const loadCompanyInfo = () => {
    if (businessUnitId && companyId) {
      businessUnitCompanyGetReq.run(businessUnitId, companyId);
    }
  };

  return {
    // State
    isConnected,
    mdmId,
    creditId,
    creditTrackingMode,
    vendorCompanyDetails,
    businessUnitCompanyDetails,
    creditInfo,
    isInvalid,
    error,

    // Setters
    setIsInvalid,

    // API requests
    businessUnitCompanyGetReq,
    mdmApplyReq,
    updateCreditReq,
    createCreditReq,

    // Utility functions
    loadCompanyInfo
  };
};

export default useCredit;
